import React, { useState, useEffect, useMemo } from 'react';
import { View, SafeAreaView, ActivityIndicator, TouchableOpacity } from 'react-native';
import ViewPortProvider from '../../hooks/ViewPortProvider';
import useViewport from '../../hooks/useViewport';
import THEME from '../../config/theme';
import { useAuthUser } from '../../contexts/useAuthUser';
import { borderRadiusButtons } from '../../config/data';
import {
  doc,
  getDoc,
  onSnapshot,
} from 'firebase/firestore';
import { firestore } from '../../services/firebase';
import { VerticalScroll } from './style';
import ViewVideo from './ViewVideo';
import ViewFlatList from './ViewFlatList';
import { StandardText } from '../../config/theme/globalStyles';
import Header from '../../components/Header';
import { useCustomTheme } from '../../contexts/useCustomTheme';

export function LiveStream({ route, navigation }) {
  const { cardId, categoryId, selectedVideo } = route.params;
  const { customColor, customFont } = useCustomTheme();

  const [card, setCard] = useState();
  const [video, setVideo] = useState(null);
  // Sessão e documento `users/{uid}` já resolvidos no boot (AuthUserProvider).
  const { user: userDoc, userId, isLogged, isLoadingUser } = useAuthUser();
  const user = useMemo(
    () => (isLogged && userDoc ? { id: userId, ...userDoc } : undefined),
    [isLogged, userDoc, userId]
  );
  const [isLoading, setIsLoading] = useState(true);

  const [isCardLoaded, setIsCardLoaded] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const isUserLoaded = !isLoadingUser;

  const toDate = (seconds) => {
    const date = new Date(1970, 0, 1);
    date.setSeconds(seconds);
    return date;
  };

  const fetchCard = async () => {
    const docRef = doc(firestore, 'categories', categoryId, 'cards', cardId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return;
    const cardData = docSnap.data();
    setCard(cardData);
    setIsCardLoaded(true);
  };


  useEffect(() => {
    const ref = doc(
      firestore,
      'categories',
      categoryId,
      'cards',
      cardId,
      'videos',
      selectedVideo
    );
    const unsubVideo = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setVideo(null);
          setIsVideoLoaded(true);
          return;
        }
        const data = snap.data() || {};
        setVideo(data);
        setIsVideoLoaded(true);
      },
      () => {
        // Em caso de erro, libera o loading para não travar a UI
        setIsVideoLoaded(true);
      }
    );
    return () => unsubVideo();
  }, [categoryId, cardId, selectedVideo]);

  // Detecta fim da live (type: livestream -> content) e replica alerta do mobile
  const prevTypeRef = React.useRef(null);
  useEffect(() => {
    if (!video) return;
    const prev = prevTypeRef.current;
    const curr = video?.type || null;
    if (prev && prev !== 'content' && curr === 'content') {
      // mesmo fluxo de UX do mobile (ajuste a rota conforme seu app)
      alert('Transmissão finalizada');
      // merge: true — sem isto, voltar pra "Aula" substitui os params da rota
      // já na pilha e apaga o productCatalogOrigin dela (ver ClickClass.js).
      navigation.navigate({
        name: 'Aula',
        params: { cardId, categoryId, selectedVideo },
        merge: true,
      });
    }
    prevTypeRef.current = curr;
  }, [video?.type, navigation, cardId, categoryId, selectedVideo]);

  // O chat é do LiveStreamChat, que ouve as mensagens por conta própria. Aqui
  // havia um segundo listener no histórico INTEIRO cujo resultado nunca chegava
  // à tela, e que nem era cancelado (o efeito devolvia uma Promise): cada
  // espectador seguia relendo o chat até fechar a aba.
  useEffect(() => {
    if (
      isCardLoaded &&
      isVideoLoaded &&
      isUserLoaded
    ) {
      setIsLoading(false);
    }
  }, [
    isCardLoaded,
    isVideoLoaded,
    isUserLoaded,
  ]);


  useEffect(() => {
    fetchCard();
  }, [categoryId, cardId, selectedVideo]);

  if (isLoading) {
    return (
      <ActivityIndicator
        style={{
          flex: 1,
          backgroundColor:
            customColor && customColor.background?.app
              ? customColor.background.app
              : THEME.COLORS.BACKGROUND_MAIN,
          justifyContent: 'center',
        }}
        color={
          customColor && customColor.primary
            ? customColor.primary
            : THEME.COLORS.PRIMARY_800
        }
      />
    );
  }


  const OutsideView = () => {
    const { width } = useViewport();
    const breakpoint = 1080;

    return width < breakpoint ? (
      <>
        <View
          style={{
            alignSelf: 'center',
            width: '100%',
            height: (width - 32) / 1.78,
            marginBottom: '1rem',
            paddingHorizontal: '2%',
          }}
        >
          <ViewVideo user={user} video={video} card={card} />
        </View>
        <VerticalScroll>
          <ViewFlatList
            videoId={selectedVideo}
            categoryId={categoryId}
            cardId={cardId}
          />
        </VerticalScroll>
      </>
    ) : (
      <View style={{ paddingHorizontal: '2%', paddingVertical: '2rem' }}>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'center',
          }}
        >
          <View
            style={{
              alignSelf: 'center',
              width: '70%',
              height: (width * 0.7 - 32) / 1.78,
            }}
          >
            <ViewVideo user={user} video={video} card={card} />
          </View>
          <View
            style={{
              alignSelf: 'center',
              width: '30%',
              height: (width * 0.7 - 32) / 1.78,
              paddingHorizontal: '1rem',
            }}
          >
            <ViewFlatList
              videoId={selectedVideo}
              categoryId={categoryId}
              cardId={cardId}
            />
          </View>
        </View>
      </View>
    );
  };

  return (
    <ViewPortProvider>
      <SafeAreaView
        style={{
          backgroundColor:
            customColor && customColor.background?.app
              ? customColor.background.app
              : THEME.COLORS.BACKGROUND_MAIN,
          flexGrow: 1,
        }}
      >
        <Header
          onPress={() => {
            // merge: true — mesmo motivo do efeito de "transmissão finalizada"
            // acima: preserva o productCatalogOrigin já gravado em "Aula".
            navigation.navigate({
              name: 'Aula',
              params: {
                cardId: cardId,
                categoryId: categoryId,
                selectedVideo: selectedVideo,
              },
              merge: true,
            });
          }}
        />
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            paddingHorizontal: '1rem',
            marginBottom: '1rem',
          }}
        >
          <StandardText
            color={
              customColor && customColor.text && customColor.text.app
                ? customColor.text.app
                : THEME.COLORS.TEXT_MAIN
            }
            textAlign="left"
            fontSize={
              customFont && customFont.fontLX ? customFont.fontLX : THEME.FONTSIZE.BIG
            }
            style={{
              fontFamily:
                customFont && customFont.fontFamilyTitle
                  ? customFont.fontFamilyTitle
                  : THEME.FONTFAMILY.BOLD,
            }}
          >
            {video?.title}
          </StandardText>
          <TouchableOpacity
            disabled={true}
            style={{
              backgroundColor:
                customColor?.button?.backgroundApp || THEME.COLORS.PRIMARY_900,
              borderRadius: borderRadiusButtons,
              padding: '0.7rem',
              marginLeft: 12, // <-- Aqui adiciona o espaçamento entre o título e o botão
            }}
          >
            <StandardText
              style={{
                fontFamily:
                  customFont && customFont.fontFamilyTitle
                    ? customFont.fontFamilyTitle
                    : THEME.FONTFAMILY.BOLD,
              }}
              color={
                customColor && customColor.button
                  ? customColor.button.textApp
                  : THEME.COLORS.TEXT_BUTTON
              }
            >
              {`AO VIVO`}
            </StandardText>
          </TouchableOpacity>
        </View>
        {(card?.productIds &&
          card?.productIds?.length > 0 &&
          card?.productIds?.filter((cardProductId) => {
            const matchingUserProducts = user?.productIds?.filter(
              (userProduct) => userProduct.productId === cardProductId
            );
            if (!matchingUserProducts || matchingUserProducts.length === 0) {
              return false; // O usuário não possui este produto, então não tem acesso
            }
            // Se não houver data de expiração, ou se alguma data de expiração for maior que a data atual, o usuário tem acesso
            return matchingUserProducts.some(
              (userProduct) =>
                !userProduct.expirationDate ||
                toDate(userProduct.expirationDate.seconds) > new Date()
            );
          }).length > 0) ||
          (card?.plans &&
            card?.plans.length > 0 &&
            card?.plans.filter(
              (plan) => (plan.priceId || plan.productId) == user?.plan
            ).length > 0) ||
          (user?.courses &&
            user?.courses.length > 0 &&
            user?.courses.filter(
              (course) =>
                card?.coursesCard?.some(
                  (itemToBeRemoved) =>
                    (itemToBeRemoved.priceId || itemToBeRemoved.productId) ===
                    (course.priceId || course.productId)
                ) && toDate(course.dueDate.seconds) > new Date()
            ).length > 0) ||
          (card?.plans == null &&
            card?.coursesCard == null &&
            card?.productIds == null) ||
          user?.isAdmin == true ? (
          <OutsideView />
        ) : (
          <View
            style={{
              justifyContent: 'center',
              flex: 1,
              alignItems: 'center',
              marginHorizontal: '0.5rem',
            }}
          >
            <StandardText
              style={{
                fontFamily:
                  customFont && customFont.fontFamilySubtitle
                    ? customFont.fontFamilySubtitle
                    : THEME.FONTFAMILY.MEDIUM,
              }}
              color={
                customColor && customColor.text && customColor.text.auth
                  ? customColor.text.auth
                  : THEME.COLORS.TEXT_ABOUT
              }
            >
              Ops, você não possui acesso a esse conteúdo!
            </StandardText>
          </View>
        )}
      </SafeAreaView>
    </ViewPortProvider>
  );
}
